"use client";
import { motion } from "framer-motion";
import { FaUserPlus, FaCheckCircle, FaUsers } from "react-icons/fa";
import SectionHeader from "../SectionHeader";
import CTA from "../../CTA";
import { useLocale, useTranslations } from "@/i18n/provider";
import { localizePath } from "@/i18n/pathnames";

const StepCard = ({
  step,
  title,
  description,
  icon,
}: {
  step: number;
  title: string;
  description: string;
  icon: React.ReactNode;
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5 }}
      className="bg-white p-6 flex flex-col items-center text-left relative"
    >
      <hr className="text-[#E1E4EA] w-full absolute mt-5" />
      <div className="flex items-center gap-4 mb-4">
        <div className="z-20 w-10 h-10 rounded-full bg-green200 flex items-center justify-center">
          <span className="text-green text-lg">{icon}</span>
        </div>

        {/* <span className="text-sm font-semibold text-gray800">STEP {step}</span> */}
      </div>
      <h3 className="text-xl font-bold text-gray800 mb-3">{title}</h3>
      <p className="text-gray500 leading-relaxed text-sm text-center">
        {description}
      </p>
    </motion.div>
  );
};

const HowItWorks = ({
  page,
  ctaTitle,
  ctaDescription,
  ctaUrl,
  buttonText,
}: {
  page: string;
  ctaTitle: string;
  ctaDescription?: string;
  ctaUrl: string;
  buttonText: string;
}) => {
  const t = useTranslations();
  const locale = useLocale();
  const partnerSteps = [
    {
      step: 1,
      title: t("partner.page.steps.registerTitle"),
      description: t("partner.page.steps.registerDescription"),
      icon: <FaUserPlus />,
    },
    {
      step: 2,
      title: t("partner.page.steps.verifyTitle"),
      description: t("partner.page.steps.verifyDescription"),
      icon: <FaCheckCircle />,
    },
    {
      step: 3,
      title: t("partner.page.steps.addTitle"),
      description: t("partner.page.steps.addDescription"),
      icon: <FaUsers />,
    },
  ];

  const creatorSteps = [
    {
      step: 1,
      title: t("creator.page.steps.accountTitle"),
      description: t("creator.page.steps.accountDescription"),
      icon: <FaUserPlus />,
    },
    {
      step: 2,
      title: t("creator.page.steps.communityTitle"),
      description: t("creator.page.steps.communityDescription"),
      icon: <FaCheckCircle />,
    },
    {
      step: 3,
      title: t("creator.page.steps.activityTitle"),
      description: t("creator.page.steps.activityDescription"),
      icon: <FaUsers />,
    },
  ];

  return (
    <section className="py-16 bg-gray-50">
      <div className="container px-4 mx-auto">
        <SectionHeader
          title={
            page === "creator"
              ? t("creator.page.howItWorksTitle")
              : t("partner.page.howItWorksTitle")
          }
          text={
            page === "creator"
              ? t("creator.page.howItWorksText")
              : t("partner.page.howItWorksText")
          }
        />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-5 mb-24">
          {(page === "creator" ? creatorSteps : partnerSteps)?.map((step) => (
            <StepCard
              key={step.step}
              step={step.step}
              title={step.title}
              description={step.description}
              icon={step.icon}
            />
          ))}
        </div>

        <CTA
          title={ctaTitle}
          text={ctaDescription}
          buttonText={buttonText}
          onClick={() => {
            window.open(localizePath(ctaUrl, locale), "_blank");
          }}
        />
      </div>
    </section>
  );
};

export default HowItWorks;
